// src/pages/consumers/ConsumerSearch.jsx

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
} from '@mui/material'
import {
  Search,
  Close,
  PictureAsPdf,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material'

import api from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import { getActiveLayout, parseLayoutConfig } from '../../utils/layoutUtils'
import ConsumerDetailLayoutRenderer from '../../components/consumers/ConsumerDetailLayoutRenderer'
import ActivityForm from '../../components/forms/ActivityForm'
import { getConsumerWorkflowResolvers } from '../../services/workflowService'
import {
  normalizeArray,
  pickValue,
  readByKeys,
  toSafeText,
} from '../../utils/defensiveData'

const buildDocumentDownloadUrl = (documentId) => {
  if (!documentId) return null

  const baseUrl = String(api.defaults.baseURL || '').replace(/\/+$/, '')
  if (!baseUrl) return null

  return `${baseUrl}/documents/${encodeURIComponent(documentId)}/file`
}

const createEmptyActivityState = () => ({
  actionCode: '',
  contactCode: '',
  resultCode: '',
  comments: '',
  documents: [],
})

const hasCustomFields = (consumer) =>
  consumer?.custom_fields &&
  typeof consumer.custom_fields === 'object' &&
  Object.keys(consumer.custom_fields).length > 0

const hasProductInUserRelations = (user, targetProductId) => {
  if (!user || targetProductId === null || targetProductId === undefined) {
    return false
  }

  const directProductId = readByKeys(user, ['product_id', 'productId'], null)
  if (directProductId !== null && String(directProductId) === String(targetProductId)) {
    return true
  }

  const relatedProducts = normalizeArray(
    readByKeys(user, ['products', 'assignedProducts', 'productList'], [])
  )

  return relatedProducts.some((entry) => {
    if (entry && typeof entry === 'object') {
      const entryId = readByKeys(entry, ['id', 'product_id', 'productId'], null)
      return entryId !== null && String(entryId) === String(targetProductId)
    }

    return String(entry) === String(targetProductId)
  })
}

const ConsumerSearch = () => {
  const navigate = useNavigate()
  const { productId } = useParams()

  const [consumers, setConsumers] = useState([])
  const [search, setSearch] = useState('')
  const [selectedConsumer, setSelectedConsumer] = useState(null)
  const [layoutConfig, setLayoutConfig] = useState([])
  const [loading, setLoading] = useState(false)
  const [layoutLoading, setLayoutLoading] = useState(false)
  const [workflowResolvers, setWorkflowResolvers] = useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  const [activities, setActivities] = useState([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [activitiesError, setActivitiesError] = useState('')

  const [docsByActivity, setDocsByActivity] = useState({})
  const [docsLoading, setDocsLoading] = useState({})
  const [openDocs, setOpenDocs] = useState({})

  const [newActivity, setNewActivity] = useState(createEmptyActivityState)
  const [activityFormResetKey, setActivityFormResetKey] = useState(0)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [productContext, setProductContext] = useState({
    product: null,
    client: null,
    usersCount: null,
    activitiesCount: null,
  })
  const [contextLoading, setContextLoading] = useState(false)

  const resetNewActivity = () => {
    setNewActivity(createEmptyActivityState())
    setActivityFormResetKey((current) => current + 1)
  }

  const fetchConsumers = async () => {
    if (!productId) {
      setConsumers([])
      setSnackbar({
        open: true,
        message: 'No se encontró un producto válido para buscar consumidores.',
        severity: 'warning',
      })
      return
    }

    try {
      setLoading(true)
      setSelectedConsumer(null)

      const res = await api.get(`/products/${productId}/consumers`, {
        params: { search },
      })

      const data = Array.isArray(res.data) ? res.data : []

      setConsumers(data)

      if (data.length === 0) {
        setSnackbar({
          open: true,
          message: 'No hay consumidores para mostrar.',
          severity: 'info',
        })
      }
    } catch (err) {
      console.error('[ConsumerSearch] Error fetching consumers:', err)
      setConsumers([])
      setSnackbar({
        open: true,
        message: 'Error al obtener consumidores.',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchProductLayout = async () => {
    if (!productId) {
      setLayoutConfig([])
      return
    }

    try {
      setLayoutLoading(true)

      const layoutData = await getLayoutsByProduct(productId)
      const payload = layoutData?.data ?? layoutData
      const layouts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.layouts)
          ? payload.layouts
          : payload
            ? [payload]
            : []

      const activeLayout = getActiveLayout(layouts)
      const normalizedConfig = parseLayoutConfig(
        activeLayout?.config ??
          activeLayout?.layoutConfig ??
          activeLayout?.fields
      )

      setLayoutConfig(normalizedConfig)

      if (!Array.isArray(normalizedConfig) || normalizedConfig.length === 0) {
        setSnackbar({
          open: true,
          message:
            'El producto no tiene un layout interpretable o no fue posible cargar su configuración.',
          severity: 'warning',
        })
      }
    } catch (err) {
      console.error('[ConsumerSearch] Error fetching layout:', err)
      setLayoutConfig([])
      setSnackbar({
        open: true,
        message: 'No se pudo cargar el layout del producto.',
        severity: 'warning',
      })
    } finally {
      setLayoutLoading(false)
    }
  }

  const fetchProductContext = async () => {
    if (!productId) {
      setProductContext({
        product: null,
        client: null,
        usersCount: null,
        activitiesCount: null,
      })
      return
    }

    try {
      setContextLoading(true)

      const productResponse = await api.get(`/products/${productId}`)
      const productPayload = productResponse?.data || null
      const clientId = pickValue(productPayload, 'client_id', 'clientId', null)

      const [clientResult, usersResult, activitiesResult] = await Promise.allSettled([
        clientId ? api.get(`/clients/${clientId}`) : Promise.resolve(null),
        api.get('/users'),
        api.get('/activities', { params: { productId, page: 1, limit: 1 } }),
      ])

      const clientPayload =
        clientResult.status === 'fulfilled' ? clientResult.value?.data || null : null

      const usersCount =
        usersResult.status === 'fulfilled'
          ? normalizeArray(usersResult.value?.data).filter((userEntry) =>
              hasProductInUserRelations(userEntry, productId)
            ).length
          : null

      let activitiesCount = null
      if (activitiesResult.status === 'fulfilled') {
        const activitiesPayload = activitiesResult.value?.data || {}
        const metaTotal = activitiesPayload?.meta?.total
        if (typeof metaTotal === 'number') {
          activitiesCount = metaTotal
        } else {
          activitiesCount = normalizeArray(activitiesPayload).length
        }
      }

      setProductContext({
        product: productPayload,
        client: clientPayload,
        usersCount,
        activitiesCount,
      })
    } catch (_err) {
      setProductContext({
        product: null,
        client: null,
        usersCount: null,
        activitiesCount: null,
      })
    } finally {
      setContextLoading(false)
    }
  }

  const fetchWorkflowResolvers = async (consumerId) => {
    if (!consumerId) {
      setWorkflowResolvers(null)
      return
    }

    try {
      const payload = await getConsumerWorkflowResolvers(consumerId, productId)
      setWorkflowResolvers(payload)
    } catch (err) {
      console.error('[ConsumerSearch] Error loading workflow resolvers:', err)
      setWorkflowResolvers(null)
    } finally {
      // Intentionally empty: resolvers are fetched on-demand and rendered only by layout widgets.
    }
  }

  const fetchActivities = async (consumerId) => {
    if (!consumerId) return

    try {
      setActivitiesLoading(true)
      setActivitiesError('')

      const res = await api.get('/activities', {
        params: { consumerId, productId },
      })

      setActivities(Array.isArray(res.data?.data) ? res.data.data : [])
      setDocsByActivity({})
      setOpenDocs({})
      setDocsLoading({})
    } catch (err) {
      console.error('[ConsumerSearch] Error loading activities:', err)
      setActivities([])
      setActivitiesError('Error al cargar actividades.')
    } finally {
      setActivitiesLoading(false)
    }
  }

  const handleSelectConsumer = (consumer) => {
    resetNewActivity()
    setSaveError('')
    setSelectedConsumer(consumer)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToList = () => {
    setSelectedConsumer(null)
    setWorkflowResolvers(null)
    setActivities([])
    setActivitiesError('')
    resetNewActivity()
    setSaveError('')
    setDocsByActivity({})
    setOpenDocs({})
    setDocsLoading({})
  }

  const handleToggleDocs = async (activityId) => {
    setOpenDocs((prev) => ({
      ...prev,
      [activityId]: !prev[activityId],
    }))

    if (docsByActivity[activityId]) return

    try {
      setDocsLoading((prev) => ({ ...prev, [activityId]: true }))

      const res = await api.get(`/activities/${activityId}/documents`)
      setDocsByActivity((prev) => ({
        ...prev,
        [activityId]: Array.isArray(res.data) ? res.data : [],
      }))
    } catch (err) {
      console.error(
        `[ConsumerSearch] Error loading docs for activity ${activityId}:`,
        err
      )
      setDocsByActivity((prev) => ({
        ...prev,
        [activityId]: [],
      }))
    } finally {
      setDocsLoading((prev) => ({ ...prev, [activityId]: false }))
    }
  }

  const handleCreateActivity = async (e) => {
    e.preventDefault()

    if (!selectedConsumer?.id) {
      setSaveError(
        'Debes seleccionar un consumidor antes de registrar la actividad.'
      )
      return
    }

    let activityCreated = false

    try {
      setSaving(true)
      setSaveError('')

      const payload = {
        type: newActivity.actionCode,
        contactId: newActivity.contactCode,
        description: newActivity.resultCode,
        comments: newActivity.comments,
        consumerId: selectedConsumer.id,
        productId: Number(productId),
      }

      const res = await api.post('/activities/register', payload)
      activityCreated = true

      const createdActivity = res.data?.data || {}
      const activityId = createdActivity.activityId || createdActivity.id
      const documents = Array.isArray(newActivity.documents)
        ? newActivity.documents
        : []

      if (documents.length > 0 && !activityId) {
        throw new Error('Created activity did not return an activity ID')
      }

      for (const file of documents) {
        const formData = new FormData()
        formData.append('document', file)

        await api.post(`/activities/${activityId}/documents`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      }

      await fetchActivities(selectedConsumer.id)
      await fetchWorkflowResolvers(selectedConsumer.id)

      resetNewActivity()

      setSnackbar({
        open: true,
        message: 'Actividad guardada correctamente.',
        severity: 'success',
      })
    } catch (err) {
      console.error('[ConsumerSearch] Error saving activity:', err)
      setSaveError(
        activityCreated
          ? 'La actividad se guardó, pero no fue posible subir uno o más documentos.'
          : 'No se pudo guardar la actividad.'
      )
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    resetNewActivity()
    setSaveError('')
    if (!productId) return
    fetchConsumers()
    fetchProductLayout()
    fetchProductContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const productName = useMemo(
    () =>
      toSafeText(
        readByKeys(productContext.product, ['name_product', 'nameProduct', 'name'], 'Producto'),
        'Producto'
      ),
    [productContext]
  )

  const productDescription = useMemo(
    () =>
      toSafeText(
        readByKeys(productContext.product, ['description', 'descriptionLabel'], 'Sin descripción operativa'),
        'Sin descripción operativa'
      ),
    [productContext]
  )

  const productStatus = useMemo(
    () =>
      toSafeText(
        readByKeys(productContext.product, ['status', 'state', 'isActive'], 'No especificado'),
        'No especificado'
      ),
    [productContext]
  )

  const productOwnerClient = useMemo(
    () =>
      toSafeText(
        readByKeys(
          productContext.client,
          ['full_name_or_company', 'fullNameOrCompany', 'name'],
          'No disponible'
        ),
        'No disponible'
      ),
    [productContext]
  )

  useEffect(() => {
    if (!selectedConsumer?.id) return
    fetchActivities(selectedConsumer.id)
    fetchWorkflowResolvers(selectedConsumer.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConsumer, productId])

  const filtered = useMemo(() => {
    if (!search) return consumers

    const s = String(search).toLowerCase()

    return consumers.filter((c) => {
      const fullName = String(c.full_name || '').toLowerCase()
      const email = String(c.email || '').toLowerCase()
      const phone = String(c.phone || '').toLowerCase()

      return fullName.includes(s) || email.includes(s) || phone.includes(s)
    })
  }, [consumers, search])

  return (
    <Container sx={{ mt: 3, mb: 6 }}>
      <Typography variant="h4" gutterBottom>
        Consumidores
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {contextLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6">{productName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {productDescription}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    ID Producto: {toSafeText(productId, 'No disponible')}
                  </Typography>
                  <Typography variant="body2">
                    Cliente: {productOwnerClient}
                  </Typography>
                  <Typography variant="body2">
                    Estado: {productStatus}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  KPIs operativos
                </Typography>
                <Typography variant="body2">
                  Consumidores: {consumers.length}
                </Typography>
                <Typography variant="body2">
                  Actividades: {productContext.activitiesCount === null ? 'No disponible' : productContext.activitiesCount}
                </Typography>
                <Typography variant="body2">
                  Usuarios vinculados: {productContext.usersCount === null ? 'No disponible' : productContext.usersCount}
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Buscar por nombre, teléfono o correo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearch('')}>
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={fetchConsumers}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={22} /> : 'Buscar'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate('/settings/consumers/create')}
              fullWidth
            >
              Crear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {!selectedConsumer && (
        <>
          {filtered.length === 0 && !loading ? (
            <Alert severity="info">No hay consumidores para mostrar.</Alert>
          ) : (
            <List sx={{ mt: 1 }}>
              {filtered.map((c) => (
                <Card key={c.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <ListItemButton onClick={() => handleSelectConsumer(c)}>
                      <ListItemText
                        primary={c.full_name || 'Sin nombre'}
                        secondary={`${c.email || 'Sin correo'} • ${c.phone || 'Sin teléfono'}`}
                      />
                    </ListItemButton>
                  </CardContent>
                </Card>
              ))}
            </List>
          )}
        </>
      )}

      {selectedConsumer && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h5">
                {selectedConsumer.full_name || 'Consumidor'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedConsumer.email || 'Sin correo'} •{' '}
                {selectedConsumer.phone || 'Sin teléfono'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => {
                  console.log('Export PDF', selectedConsumer.id)
                }}
              >
                <PictureAsPdf />
              </IconButton>

              <IconButton
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                <KeyboardArrowUp />
              </IconButton>

              <Button variant="outlined" onClick={handleBackToList}>
                Volver a la lista
              </Button>
            </Box>
          </Box>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del consumidor
              </Typography>

              {layoutLoading ? (
                <CircularProgress />
              ) : Array.isArray(layoutConfig) && layoutConfig.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <ConsumerDetailLayoutRenderer
                    layoutConfig={layoutConfig}
                    consumerData={{
                      ...selectedConsumer,
                      workflowPayload: workflowResolvers || null,
                      workflowResolverPayload:
                        workflowResolvers?.resolvers || null,
                      workflowResolversRaw:
                        workflowResolvers?.resolvers?.workflow || {},
                      workflow: workflowResolvers?.resolvers?.workflow || {},
                      workflowResolvers: workflowResolvers?.resolvers?.workflow
                        ? {
                            current_stage:
                              workflowResolvers.resolvers.workflow.current_stage
                                ?.name || '',
                            current_billable_stage:
                              workflowResolvers.resolvers.workflow
                                .current_billable_stage?.name || '',
                            days_since_last_comment:
                              workflowResolvers.resolvers.workflow.metrics
                                ?.days_since_last_comment?.value ?? '',
                            days_since_last_document:
                              workflowResolvers.resolvers.workflow.metrics
                                ?.days_since_last_document?.value ?? '',
                          }
                        : {},
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Campos personalizados
                  </Typography>

                  <Grid container spacing={1}>
                    {hasCustomFields(selectedConsumer) ? (
                      Object.entries(selectedConsumer.custom_fields).map(
                        ([k, v]) => (
                          <Grid item xs={12} md={6} key={k}>
                            <Typography variant="body2">
                              <strong>{k}:</strong> {String(v ?? '')}
                            </Typography>
                          </Grid>
                        )
                      )
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Sin campos personalizados.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box component="form" onSubmit={handleCreateActivity} sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Nueva actividad
            </Typography>

            <ActivityForm
              key={activityFormResetKey}
              productId={productId}
              initialData={{
                actionCode: newActivity.actionCode,
                contactCode: newActivity.contactCode,
                resultCode: newActivity.resultCode,
                comments: newActivity.comments,
                documents: newActivity.documents,
              }}
              onChange={(formData) =>
                setNewActivity({
                  actionCode: formData.actionCode || '',
                  contactCode: formData.contactCode || '',
                  resultCode: formData.resultCode || '',
                  comments: formData.comments || '',
                  documents: formData.documents || [],
                })
              }
            />

            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ mt: 2 }}
            >
              {saving ? 'Guardando...' : 'Guardar actividad'}
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6">Historial de actividades</Typography>

            {activitiesLoading ? (
              <CircularProgress sx={{ mt: 2 }} />
            ) : activitiesError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {activitiesError}
              </Alert>
            ) : activities.length > 0 ? (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>Tipo</b>
                      </TableCell>
                      <TableCell>
                        <b>Resultado</b>
                      </TableCell>
                      <TableCell>
                        <b>Usuario</b>
                      </TableCell>
                      <TableCell>
                        <b>Fecha</b>
                      </TableCell>
                      <TableCell>
                        <b>Documentos</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {activities.map((act) => {
                      const activityId = act.activityId || act.id

                      return (
                        <React.Fragment key={activityId}>
                          <TableRow>
                            <TableCell>
                              {act.typeLabel || act.type || '-'}
                            </TableCell>
                            <TableCell>
                              {act.descriptionLabel || act.description || '-'}
                            </TableCell>
                            <TableCell>{act.createdByName || '-'}</TableCell>
                            <TableCell>
                              {act.dateTime
                                ? new Date(act.dateTime).toLocaleString()
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleDocs(activityId)}
                              >
                                {openDocs[activityId] ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={5}
                            >
                              <Collapse
                                in={openDocs[activityId]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box margin={1}>
                                  {docsLoading[activityId] ? (
                                    <CircularProgress size={20} />
                                  ) : docsByActivity[activityId]?.length > 0 ? (
                                    docsByActivity[activityId].map((doc) => {
                                      const downloadUrl =
                                        buildDocumentDownloadUrl(doc.id)

                                      return downloadUrl ? (
                                        <Link
                                          key={doc.id}
                                          href={downloadUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          display="block"
                                        >
                                          {doc.filename}
                                        </Link>
                                      ) : (
                                        <Typography key={doc.id} variant="body2">
                                          {doc.filename}
                                        </Typography>
                                      )
                                    })
                                  ) : (
                                    <Typography variant="body2">
                                      No hay documentos.
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ mt: 2 }}>
                No hay actividades registradas para este consumidor.
              </Typography>
            )}
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  )
}

export default ConsumerSearch
